import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Upload, FileText, Loader2, CheckCircle2, AlertCircle, ShieldCheck, Clock } from "lucide-react";
import { ASSETS } from "../mock";
import { compressImageIfNeeded } from "../utils/compressImage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const T = {
  es: {
    title: "Reenvío de documento",
    intro: "Sube el documento corregido para continuar con tu solicitud.",
    doc: "Documento solicitado", reason: "Motivo del rechazo", ref: "Solicitud",
    choose: "Seleccionar archivo", formats: "PDF, JPG o PNG · máx. 1.5MB (las fotos se comprimen automáticamente)",
    send: "Enviar documento", sending: "Enviando…",
    okTitle: "¡Documento recibido!", okText: "Nuestro equipo lo verificará y te avisaremos por correo. Puedes cerrar esta página.",
    used: "Este enlace ya fue utilizado. Si necesitas enviar otro documento, responde al correo que recibiste.",
    expired: "Este enlace ha expirado. Escríbenos a info@rapidpaypro.com para recibir uno nuevo.",
    closed: "Tu solicitud ya fue cerrada; no es necesario enviar más documentos.",
    invalid: "Enlace inválido. Verifica que copiaste la dirección completa del correo.",
    secure: "Enlace seguro y personal", validUntil: "Válido hasta",
    back: "Ir a rapidpaypro.com",
  },
  en: {
    title: "Document resubmission",
    intro: "Upload the corrected document to continue with your application.",
    doc: "Requested document", reason: "Rejection reason", ref: "Application",
    choose: "Choose file", formats: "PDF, JPG or PNG · max 1.5MB (photos are compressed automatically)",
    send: "Send document", sending: "Sending…",
    okTitle: "Document received!", okText: "Our team will verify it and let you know by email. You can close this page.",
    used: "This link has already been used. If you need to send another document, reply to the email you received.",
    expired: "This link has expired. Write to info@rapidpaypro.com to get a new one.",
    closed: "Your application has been closed; no more documents are needed.",
    invalid: "Invalid link. Make sure you copied the full address from the email.",
    secure: "Secure personal link", validUntil: "Valid until",
    back: "Go to rapidpaypro.com",
  },
  pt: {
    title: "Reenvio de documento",
    intro: "Envie o documento corrigido para continuar com sua solicitação.",
    doc: "Documento solicitado", reason: "Motivo da rejeição", ref: "Solicitação",
    choose: "Escolher arquivo", formats: "PDF, JPG ou PNG · máx. 1,5MB (fotos são comprimidas automaticamente)",
    send: "Enviar documento", sending: "Enviando…",
    okTitle: "Documento recebido!", okText: "Nossa equipe irá verificá-lo e avisaremos por e-mail. Você pode fechar esta página.",
    used: "Este link já foi utilizado. Se precisar enviar outro documento, responda ao e-mail recebido.",
    expired: "Este link expirou. Escreva para info@rapidpaypro.com para receber um novo.",
    closed: "Sua solicitação já foi encerrada; não é necessário enviar mais documentos.",
    invalid: "Link inválido. Verifique se copiou o endereço completo do e-mail.",
    secure: "Link seguro e pessoal", validUntil: "Válido até",
    back: "Ir para rapidpaypro.com",
  },
};

export default function KycResubmit() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("loading");
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/kyc/resubmit/${token}`)
      .then((r) => {
        setInfo(r.data);
        if (r.data.resubmitted) setState("used");
        else if (r.data.closed) setState("closed");
        else if (r.data.expired) setState("expired");
        else setState("form");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const t = T[info?.language] || T.es;

  const submit = async () => {
    if (!file) return;
    setSending(true);
    setError("");
    try {
      const prepared = await compressImageIfNeeded(file);
      const data = new FormData();
      data.append("file", prepared);
      await axios.post(`${API}/kyc/resubmit/${token}`, data);
      setState("done");
    } catch (err) {
      setError(err.response?.data?.detail || "No se pudo enviar el archivo. Intenta de nuevo.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="font-montserrat min-h-screen bg-[#fafcf9] flex flex-col">
      <header className="bg-white border-b border-black/[0.06]">
        <div className="max-w-2xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/"><img src={ASSETS.logo} alt="RapidPay" className="h-8 w-auto" /></Link>
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-black/50"><ShieldCheck size={14} className="text-[#0fb013]" /> {t.secure}</span>
        </div>
      </header>

      <main className="flex-1 max-w-2xl w-full mx-auto px-5 py-10 sm:py-14">
        {state === "loading" && (
          <div className="flex justify-center py-20"><Loader2 size={28} className="animate-spin text-[#0fb013]" /></div>
        )}

        {["invalid", "used", "expired", "closed"].includes(state) && (
          <div data-testid="resubmit-state" className="bg-white border border-black/[0.06] rounded-3xl p-8 sm:p-10 text-center">
            <AlertCircle size={40} className="mx-auto text-[#ff603e]" />
            <p className="mt-4 text-[15px] font-bold text-black/75 leading-relaxed">{t[state]}</p>
            <a href="https://rapidpaypro.com" className="mt-6 inline-flex text-[13px] font-bold text-[#0fb013] underline">{t.back}</a>
          </div>
        )}

        {state === "done" && (
          <div data-testid="resubmit-success" className="bg-white border border-black/[0.06] rounded-3xl p-8 sm:p-10 text-center">
            <span className="mx-auto w-16 h-16 rounded-full bg-[#0fe814]/15 flex items-center justify-center"><CheckCircle2 size={34} className="text-[#0fb013]" /></span>
            <h1 className="mt-5 text-2xl font-extrabold text-black">{t.okTitle}</h1>
            <p className="mt-2 text-[14px] font-medium text-black/60 leading-relaxed">{t.okText}</p>
            {info?.reference && <p className="mt-4 text-[12px] font-bold text-[#0fb013]">{t.ref}: {info.reference}</p>}
          </div>
        )}

        {state === "form" && info && (
          <div data-testid="resubmit-form" className="bg-white border border-black/[0.06] rounded-3xl p-6 sm:p-10">
            <p className="text-[11px] font-bold uppercase tracking-wider text-[#0fb013]">{t.ref} {info.reference} · {info.business}</p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-extrabold text-black">{t.title}</h1>
            <p className="mt-2 text-[14px] font-medium text-black/60">{t.intro}</p>

            <div className="mt-6 rounded-2xl bg-[#fff4f1] border border-[#ffd2c6] px-5 py-4">
              <p className="text-[10px] font-bold uppercase tracking-wider text-black/45">{t.doc}</p>
              <p data-testid="resubmit-category" className="mt-0.5 text-[16px] font-extrabold text-black">{info.category_label}</p>
              {info.comment && (
                <p className="mt-2 text-[13px] font-medium text-black/70"><b>{t.reason}:</b> {info.comment}</p>
              )}
            </div>

            <input ref={inputRef} data-testid="resubmit-file" type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setError(""); }} />

            <button
              type="button"
              data-testid="resubmit-choose"
              onClick={() => inputRef.current?.click()}
              className={`mt-6 w-full rounded-2xl border-2 border-dashed px-5 py-8 text-center transition-colors ${file ? "border-[#0fb013] bg-[#0fe814]/5" : "border-black/15 hover:border-[#0fb013] bg-[#fafcf9]"}`}
            >
              {file ? (
                <span className="inline-flex items-center gap-2 text-[14px] font-bold text-black"><FileText size={18} className="text-[#0fb013]" /> {file.name} <span className="text-[11px] text-black/45 font-semibold">({(file.size / 1024 / 1024).toFixed(2)} MB)</span></span>
              ) : (
                <span className="inline-flex flex-col items-center gap-2 text-[14px] font-bold text-black/60"><Upload size={22} className="text-[#0fb013]" /> {t.choose}</span>
              )}
              <span className="block mt-2 text-[11px] font-medium text-black/40">{t.formats}</span>
            </button>

            {error && <p data-testid="resubmit-error" className="mt-3 text-[12px] font-bold text-[#ff603e] inline-flex items-center gap-1.5"><AlertCircle size={13} /> {error}</p>}

            <button
              type="button"
              data-testid="resubmit-submit"
              onClick={submit}
              disabled={!file || sending}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 text-[14px] font-bold text-black bg-[#0fe814] hover:bg-[#0fb013] px-6 py-3.5 rounded-full transition-colors shadow-[0_5px_16px_rgba(15,232,20,0.35)] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sending ? <><Loader2 size={16} className="animate-spin" /> {t.sending}</> : <><Upload size={16} /> {t.send}</>}
            </button>

            {info.expires_at && (
              <p className="mt-4 text-center text-[11px] font-semibold text-black/40 inline-flex w-full justify-center items-center gap-1.5">
                <Clock size={12} /> {t.validUntil} {new Date(info.expires_at).toLocaleDateString(info.language === "en" ? "en-US" : info.language === "pt" ? "pt-BR" : "es-PA", { dateStyle: "long" })}
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
